import { ATTENTION_COLOR } from '@any-ui-react/core';
import { type NotificationData, notifications } from '@any-ui-react/notifications';
import type { ReactNode } from 'react';

import { AlertType } from '@/common/utils/constants';
import { MaterialIcons } from '../material-icons';

type ToastOptions = Omit<NotificationData, 'message'>;
type UpdateToastOptions = Omit<ToastOptions, 'id'>;

interface ToastCommonProps {
  title?: ReactNode;
  message?: ReactNode;
}

const Title = ({ type, title }: { type: AlertType; title?: ReactNode }) => {
  if (title === null) {
    return null;
  }

  const defaultTitle = (type: AlertType) => {
    switch (type) {
      case AlertType.SUCCESS:
        return 'Success';
      case AlertType.WARNING:
        return 'Warning';
      case AlertType.ERROR:
        return 'Error';
      default:
        return 'Information';
    }
  };
  return <>{title ? title : defaultTitle(type)}</>;
};

const Message = ({ type, message }: { type: AlertType; message?: ReactNode }) => {
  if (message === null) {
    return null;
  }

  const defaultMessage = (type: AlertType) => {
    switch (type) {
      case AlertType.SUCCESS:
        return 'Operation Succeeded';
      case AlertType.WARNING:
        return 'Operation Warning';
      case AlertType.ERROR:
        return 'Operation Failed';
      default:
        return 'Operation Information';
    }
  };

  return <>{message ? message : defaultMessage(type)}</>;
};

const getInfoToastProps = ({ title, message }: ToastCommonProps): NotificationData => ({
  color: ATTENTION_COLOR.info,
  icon: <MaterialIcons iconName="info" fill={true} fontSize="24px" />,
  title: <Title title={title} type={AlertType.INFO} />,
  message: <Message message={message} type={AlertType.INFO} />,
});

const getSuccessToastProps = ({ title, message }: ToastCommonProps): NotificationData => ({
  color: ATTENTION_COLOR.success,
  icon: <MaterialIcons iconName="check_circle" fill={true} fontSize="24px" />,
  title: <Title title={title} type={AlertType.SUCCESS} />,
  message: <Message message={message} type={AlertType.SUCCESS} />,
});

const getWarningToastProps = ({ title, message }: ToastCommonProps): NotificationData => ({
  color: ATTENTION_COLOR.warning,
  icon: <MaterialIcons iconName="warning" fill={true} fontSize="24px" />,
  title: <Title title={title} type={AlertType.WARNING} />,
  message: <Message message={message} type={AlertType.WARNING} />,
});

const getErrorToastProps = ({ title, message }: ToastCommonProps): NotificationData => ({
  color: ATTENTION_COLOR.error,
  icon: <MaterialIcons iconName="error" fill={true} fontSize="24px" />,
  title: <Title title={title} type={AlertType.ERROR} />,
  message: <Message message={message} type={AlertType.ERROR} />,
});

const toast = (message: ReactNode, options?: ToastOptions) => {
  notifications.show({
    message,
    ...options,
  });
};

toast.info = (message?: ReactNode, options?: ToastOptions) => {
  const { title, ...others } = options || {};

  notifications.show({
    ...getInfoToastProps({ title, message }),
    ...others,
  });
};

toast.success = (message?: ReactNode, options?: ToastOptions) => {
  const { title, ...others } = options || {};

  notifications.show({
    ...getSuccessToastProps({ title, message }),
    ...others,
  });
};

toast.warning = (message?: ReactNode, options?: ToastOptions) => {
  const { title, ...others } = options || {};

  notifications.show({
    ...getWarningToastProps({ title, message }),
    ...others,
  });
};

toast.error = (message?: ReactNode, options?: ToastOptions) => {
  const { title, ...others } = options || {};

  notifications.show({
    ...getErrorToastProps({ title, message }),
    ...others,
  });
};

toast.update = (id: string) => ({
  blank: (message: ReactNode, options?: UpdateToastOptions) => {
    notifications.update({
      id,
      message,
      ...options,
    });
  },
  info: (message?: ReactNode, options?: UpdateToastOptions) => {
    const { title, ...others } = options || {};
    notifications.update({
      id,
      ...getInfoToastProps({ title, message }),
      ...others,
    });
  },
  success: (message?: ReactNode, options?: UpdateToastOptions) => {
    const { title, ...others } = options || {};
    notifications.update({
      id,
      ...getSuccessToastProps({ title, message }),
      ...others,
    });
  },
  warning: (message?: ReactNode, options?: UpdateToastOptions) => {
    const { title, ...others } = options || {};
    notifications.update({
      id,
      ...getWarningToastProps({ title, message }),
      ...others,
    });
  },
  error: (message?: ReactNode, options?: UpdateToastOptions) => {
    const { title, ...others } = options || {};
    notifications.update({
      id,
      ...getErrorToastProps({ title, message }),
      ...others,
    });
  },
});
toast.hide = notifications.hide;
toast.clean = notifications.clean;
toast.cleanQueue = notifications.cleanQueue;

export { toast };
